import type { ScoutlyticsUser } from '../types/user';

export type EngineAccessMode = 'open' | 'closed_beta' | 'disabled';

const VALID_MODES = new Set<EngineAccessMode>(['open', 'closed_beta', 'disabled']);

/** Build-time engine access mode (CRA inlines REACT_APP_* at compile time). */
export const BUILD_ENGINE_ACCESS_MODE: EngineAccessMode = (() => {
  const raw = (process.env.REACT_APP_ENGINE_ACCESS_MODE || 'open').trim().toLowerCase();
  if (VALID_MODES.has(raw as EngineAccessMode)) {
    return raw as EngineAccessMode;
  }
  return 'open';
})();

/** Build-time file upload flag — explicit `true` only. */
export const BUILD_FILE_UPLOAD_ENABLED =
  process.env.REACT_APP_ENABLE_FILE_UPLOAD === 'true';

/** Inlined nav/CTA gate: film upload shell visible only when both flags allow. */
export const SHOW_FILM_UPLOAD_ENTRY_POINTS =
  BUILD_ENGINE_ACCESS_MODE === 'open' && BUILD_FILE_UPLOAD_ENABLED;

/** Inlined nav/CTA gate: League Stream Center when film upload shell is hidden. */
export const SHOW_STREAM_CENTER_ENTRY_POINTS = !SHOW_FILM_UPLOAD_ENTRY_POINTS;

export function getEngineAccessMode(): EngineAccessMode {
  return BUILD_ENGINE_ACCESS_MODE;
}

export function getEngineWaitlistUrl(): string {
  return (process.env.REACT_APP_ENGINE_WAITLIST_URL || '').trim();
}

export function resolveEngineWaitlistHref(): string {
  const configured = getEngineWaitlistUrl();
  if (configured) {
    return configured;
  }
  return 'mailto:support@scoutlytics.gg';
}

/** True when Film Intelligence engine UX is fully open (not closed beta / disabled). */
export function isEngineOpen(): boolean {
  return BUILD_ENGINE_ACCESS_MODE === 'open';
}

/** @deprecated Prefer isEngineOpen — kept for existing call sites during migration. */
export function isEngineUxOpen(): boolean {
  return isEngineOpen();
}

/** Build-time flag: explicit `true` only; defaults to false when unset. */
export function isFileUploadEnabled(): boolean {
  return BUILD_FILE_UPLOAD_ENABLED;
}

/** Nav/CTA upload affordances (Start Analysis, Upload nav, upload feature cards). */
export function shouldShowFilmUploadEntryPoints(): boolean {
  return SHOW_FILM_UPLOAD_ENTRY_POINTS;
}

/** Nav/CTA League Stream Center affordances when film upload is gated off. */
export function shouldShowStreamCenterEntryPoints(): boolean {
  return SHOW_STREAM_CENTER_ENTRY_POINTS;
}

/** True when Film Intelligence routes should show private-beta UX instead of engine pages. */
export function shouldBlockEngineRoute(
  mode: EngineAccessMode,
  user: ScoutlyticsUser | null,
  loading: boolean
): boolean {
  if (mode === 'open') {
    return false;
  }
  if (loading) {
    return true;
  }
  return user?.engine_access_allowed !== true;
}
